/**
 * `demo/guide/autoplay.ts` - Autoplay for the demo guide (#2824).
 *
 * Autoplay plays a flow by itself: each step waits a readable pause, then the
 * guide does what the visitor would do (open the page, press the lit control,
 * send the request on the phone). It presses the real controls through their
 * `data-tour` anchors. Presses made here are marked, so the guide can tell
 * them from the visitor's own clicks (a real click pauses Autoplay).
 */
import { apiFetch } from "../../../api/client";
import type { DemoChangeToMake } from "../../../api/useDemoChangeToMake";
import { closeSidePanels, sidePanelOnPage } from "../../../lib/sidePanels";
import { DEMO_CHANGE_CONTROL_ANCHOR, PEOPLE_ADD_MEMBER_ANCHOR } from "../DemoChangedTag";
import { activeFrameOf, activePhoneFrame } from "../phoneFramePool";
import { pointAndAct, pointerGeneration, pointerHolds } from "./autoplayPointer";
import { type DemoGuideAction, showDemoPhonePerson } from "./events";
import { stripMarks } from "./marks";
import type { GuideStep } from "./types";

/** Every step waits this long before Autoplay moves on (the progress line fills over it). */
export const AUTOPLAY_STEP_MS = 3600;
/** The first-visit introduction runs each card about 20% shorter (3600 x 0.8, rounded). */
export const INTRO_STEP_MS = 2900;
/** No step waits longer than this, reduced motion included. */
export const MAX_PAUSE_MS = 4000;
/**
 * A step the guide opens a page for needs only a short beat. Its card is not
 * on screen while Autoplay opens the page, so no progress line shows it.
 */
export const OPEN_PAUSE_MS = 900;
/**
 * How often a step's follow-on presses look for their next control (#2962):
 * often, so each follow-on goes as soon as the last one has drawn.
 */
const POLL_MS = 100;
/** An action that does not finish in this long is skipped past. */
export const ACTION_GIVE_UP_MS = 9000;
/** A solve runs on the server: Autoplay waits this long for it to finish. */
export const SOLVE_GIVE_UP_MS = 90_000;
/** A pressed link that has not moved the address in this long is opened directly. */
export const ROUTE_GIVE_UP_MS = 2500;

/** The longest a long card's bar runs, reduced motion included. */
export const MAX_READING_MS = 7000;
/** Reading time per word of the card's visible text (a quick read of a short card). */
export const READ_MS_PER_WORD = 73;

/** Time to read a card: its title and body, the bold markers cut out. */
export function readingMs(step?: Pick<GuideStep, "title" | "body">): number {
  if (!step) return 0;
  const words = stripMarks(`${step.title} ${step.body}`).split(/\s+/).filter(Boolean).length;
  return Math.min(MAX_READING_MS, words * READ_MS_PER_WORD);
}

/**
 * The step duration (#2962): every progress line runs at least the base
 * (AUTOPLAY_STEP_MS, or INTRO_STEP_MS in the introduction). A long card runs
 * longer, up to its reading time. Reduced motion scales by one constant.
 */
export function autoplayPauseMs(
  step?: Pick<GuideStep, "title" | "body">,
  reduced = false,
  intro = false,
): number {
  const base = intro ? INTRO_STEP_MS : AUTOPLAY_STEP_MS;
  const read = readingMs(step);
  if (!reduced) return Math.max(base, read);
  const slow = Math.min(MAX_PAUSE_MS, Math.round(base * 1.5));
  return Math.max(slow, Math.min(MAX_READING_MS, Math.round(read * 1.5)));
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

let synthetic = false;

/** True while Autoplay itself is pressing something. */
export function isAutoplayClick(): boolean {
  return synthetic;
}

/** Press an element as Autoplay: the click is marked as the guide's own. */
export function pressElement(el: HTMLElement | null | undefined): boolean {
  return press(el);
}

/**
 * Press as Autoplay and wait for it: the pointer glides to `el`, shows the
 * press, then the click fires. True once the click fired.
 */
export function pressAndWait(el: HTMLElement | null | undefined): Promise<boolean> {
  if (!el) return Promise.resolve(false);
  return pointAndAct(el, () => click(el));
}

function click(el: HTMLElement): void {
  synthetic = true;
  try {
    el.click();
  } finally {
    synthetic = false;
  }
}

/**
 * Every Autoplay press goes through here (#2962): while Autoplay plays, the
 * pointer glides to the element first and the click fires after its press
 * beat. True when the press fires or is on its way.
 */
function press(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  // A dry run (#2962) only notes what would be pressed.
  if (dryRun) {
    dryRun.push(el);
    return true;
  }
  void pointAndAct(el, () => click(el));
  return true;
}

/** While set, `press` only notes the element: the pointer can go to it before the press. */
let dryRun: HTMLElement[] | null = null;

/** The first element `fn` would press, with nothing pressed. */
function firstPressOf(fn: () => void): HTMLElement | null {
  const before = dryRun;
  dryRun = [];
  try {
    fn();
    return dryRun[0] ?? null;
  } catch {
    return null;
  } finally {
    dryRun = before;
  }
}

function shown(el: HTMLElement): boolean {
  return el.getClientRects().length > 0 || el.offsetParent !== null || el.isConnected;
}

/**
 * Close any open side panel, then run `fn` once it is off the page (#2824), so
 * the demo phone never opens over a panel. Runs at once when none is open.
 */
export function afterSidePanelsClose(fn: () => void): void {
  closeSidePanels();
  if (!sidePanelOnPage()) {
    fn();
    return;
  }
  const started = Date.now();
  const tick = () => {
    if (!sidePanelOnPage() || Date.now() - started > 2000) fn();
    else window.setTimeout(tick, 80);
  };
  window.setTimeout(tick, 80);
}

/** Open the phone by its dock toggle, after any side panel has closed. */
export function pressPhoneToggle(): void {
  afterSidePanelsClose(() => {
    pressAnchor("demo-phone-toggle");
  });
}

/** Press the first anchor of that name that is on the page. */
export function pressAnchor(name: string): boolean {
  const all = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`));
  return press(all.find(shown));
}

function pressTestId(id: string): boolean {
  return press(document.querySelector<HTMLElement>(`[data-testid="${id}"]`));
}

/**
 * Run `tryOnce` at once, then every POLL_MS until it returns true, or `maxMs` pass. A tick
 * waits while the pointer moves (that time does not count), and the poll
 * stops when Autoplay pauses or stops.
 */
function poll(tryOnce: () => boolean, maxMs: number): void {
  const started = Date.now();
  const gen = pointerGeneration();
  let held = 0;
  const tick = () => {
    if (pointerGeneration() !== gen) return;
    if (pointerHolds()) {
      held += POLL_MS;
      window.setTimeout(tick, POLL_MS);
      return;
    }
    if (tryOnce() || Date.now() - started - held > maxMs) return;
    window.setTimeout(tick, POLL_MS);
  };
  // The first try runs at once: the step's press lands as the pointer arrives (#2962).
  tick();
}

function buttonByText(root: ParentNode, pattern: RegExp): HTMLElement | null {
  const buttons = Array.from(root.querySelectorAll<HTMLElement>("button, a, [role=button]"));
  return buttons.find((b) => pattern.test((b.textContent ?? "").trim())) ?? null;
}

/**
 * The run button of the solve console, docked beside the roster or in the
 * dialog. Else the dialog's last button that is not Cancel or Close.
 */
function solveConfirm(): HTMLElement | null {
  const run = Array.from(
    document.querySelectorAll<HTMLElement>('[data-testid^="solve-trigger-button"]'),
  ).find((b) => shown(b) && !b.hasAttribute("disabled"));
  if (run) return run;
  const modal = document.querySelector<HTMLElement>('[data-testid="solve-cycle-modal"]');
  if (!modal) return null;
  const buttons = Array.from(modal.querySelectorAll<HTMLElement>("button")).filter(
    (b) => !/cancel|close|back/i.test(b.textContent ?? "") && !b.hasAttribute("disabled"),
  );
  return buttons[buttons.length - 1] ?? null;
}

/**
 * The member app frame to press in. With a member (the scenario's own
 * person) only that person's active frame counts: never another person's.
 * Without one, the visible frame.
 */
export function visiblePhoneFrame(memberId?: string | null): HTMLIFrameElement | null {
  if (memberId) return activeFrameOf(memberId);
  const active = activePhoneFrame();
  if (active && active.style.visibility !== "hidden") return active;
  const all = Array.from(
    document.querySelectorAll<HTMLIFrameElement>('iframe[title="Member app"]'),
  );
  return (
    all.find((f) => f.style.visibility !== "hidden" && f.getAttribute("aria-hidden") !== "true") ??
    null
  );
}

const PHONE_ENTRY = /report off sick|request leave/i;

function byTestId(doc: Document, id: string): HTMLElement | null {
  return doc.querySelector<HTMLElement>(`[data-testid="${id}"]`);
}

function ready(el: HTMLElement | null): el is HTMLElement {
  return (
    Boolean(el) && !el?.hasAttribute("disabled") && el?.getAttribute("aria-disabled") !== "true"
  );
}

/** The month pager of a phone screen: the next page when `iso` is later than what shows. */
function pageToward(
  doc: Document,
  iso: string,
  cellPrefix: string,
  next: HTMLElement | null,
  prev: HTMLElement | null,
): boolean {
  const shownDays = Array.from(doc.querySelectorAll<HTMLElement>(`[data-testid^="${cellPrefix}"]`))
    .map((c) => (c.getAttribute("data-testid") ?? "").slice(cellPrefix.length))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort();
  if (shownDays.length === 0) return false;
  return press(iso > (shownDays[shownDays.length - 1] ?? "") ? next : prev);
}

/** The give-up of a seat on the phone: the day on the Calendar, its shift, Request swap, Send request. */
function giveUpStep(doc: Document, q: URLSearchParams): boolean {
  const send = byTestId(doc, "swap-modal-submit");
  if (send) {
    if (ready(send)) press(send);
    return ready(send);
  }
  const trigger = byTestId(doc, "event-sheet-swap");
  if (ready(trigger)) {
    press(trigger);
    return false;
  }
  const from = q.get("from") ?? "";
  if (!byTestId(doc, "calendar-header")) {
    press(byTestId(doc, "member-app-tab-calendar"));
    return false;
  }
  const day = byTestId(doc, `month-day-${from}`);
  if (!day) {
    // Not the month view, or another month: show Month, then page to the day.
    if (!doc.querySelector('[data-testid^="month-day-"]')) {
      press(byTestId(doc, "calendar-view-month"));
      return false;
    }
    pageToward(
      doc,
      from,
      "month-day-",
      byTestId(doc, "calendar-page-next"),
      byTestId(doc, "calendar-page-prev"),
    );
    return false;
  }
  if (day.getAttribute("data-selected") !== "true") {
    press(day);
    return false;
  }
  // The day's shifts: the one at the change's station first.
  const card = doc.querySelector<HTMLElement>('[data-testid="month-detail-card"]') ?? doc.body;
  const shifts = Array.from(
    card.querySelectorAll<HTMLElement>('[data-testid^="day-strip-event-"]'),
  );
  const station = (q.get("station") ?? "").toLowerCase();
  const shift =
    shifts.find((s) => station && (s.textContent ?? "").toLowerCase().includes(station)) ??
    shifts[0];
  press(shift);
  return false;
}

/** The leave or sick form on the phone: the kind, the offered days, then send. */
function requestFormStep(doc: Document, q: URLSearchParams): boolean {
  const mc = q.get("type") === "mc";
  const from = q.get("from") ?? "";
  const to = q.get("to") ?? from;
  const kind = Array.from(
    doc.querySelectorAll<HTMLElement>(
      '[data-testid^="request-category-"], [data-testid^="me-leaves-type-"]',
    ),
  );
  const want = mc
    ? (kind.find((c) => /-(mc)$/.test(c.getAttribute("data-testid") ?? "")) ??
      kind.find((c) => /sick|\bmc\b/i.test(c.textContent ?? "")))
    : (kind.find((c) => /-(leave)$/.test(c.getAttribute("data-testid") ?? "")) ??
      kind.find((c) => /leave/i.test(c.textContent ?? "")));
  if (want && want.getAttribute("aria-pressed") !== "true") {
    press(want);
    return false;
  }
  const picked = Array.from(
    doc.querySelectorAll<HTMLElement>('[data-testid^="request-day-"][aria-pressed="true"]'),
  ).map((c) => (c.getAttribute("data-testid") ?? "").slice("request-day-".length));
  const exact = picked.length > 0 && picked[0] === from && picked[picked.length - 1] === to;
  const send = byTestId(doc, "me-leaves-submit");
  if (exact || (picked.length > 0 && !from)) {
    if (ready(send)) press(send);
    return ready(send);
  }
  if (!from) return false;
  const first = byTestId(doc, `request-day-${from}`);
  if (!first) {
    const next = doc.querySelector<HTMLElement>('[aria-label="Next month"]');
    const prev = doc.querySelector<HTMLElement>('[aria-label="Previous month"]');
    pageToward(doc, from, "request-day-", next, prev);
    return false;
  }
  // A tap sets the start, a later tap the end. A pick that is not the offered
  // one starts again from the first day.
  if (picked.length === 0 || picked[0] !== from || picked.length > 1) {
    press(first);
    return false;
  }
  if (to !== from) {
    const last = byTestId(doc, `request-day-${to}`);
    if (last) press(last);
    else
      pageToward(
        doc,
        to,
        "request-day-",
        doc.querySelector<HTMLElement>('[aria-label="Next month"]'),
        null,
      );
  }
  return false;
}

/** Open the phone (if folded) and send the offered request on its real screens. */
function sendOnPhone(memberId?: string | null): void {
  let askedPerson = 0;
  let lastPress = 0;
  let sent = false;
  poll(() => {
    if (sent) return true;
    const frame = visiblePhoneFrame(memberId);
    const doc = frame?.contentDocument;
    if (!document.querySelector('[data-testid="demo-phone-view"]')) {
      if (!sidePanelOnPage()) pressAnchor("demo-phone-toggle");
      else closeSidePanels();
      return false;
    }
    // The phone shows another person: ask it to show the scenario's own.
    if (memberId && !frame && Date.now() - askedPerson > 1500) {
      askedPerson = Date.now();
      showDemoPhonePerson(memberId);
    }
    // Wait until the frame has drawn the member app (its tab bar is there).
    if (!frame || !doc || doc.readyState !== "complete") return false;
    if (!doc.querySelector('[data-testid="member-app-tab-tasks"]')) return false;
    // One press per beat, so each screen has drawn before the next press.
    if (Date.now() - lastPress < 600) return false;
    lastPress = Date.now();
    sent = phoneStep(frame, doc);
    return sent;
  }, ACTION_GIVE_UP_MS - 1000);
}

/** One press on the phone toward the offered request. True once Send is pressed. */
function phoneStep(frame: HTMLIFrameElement, doc: Document): boolean {
  const q = new URL(frame.getAttribute("src") ?? "", window.location.origin).searchParams;
  if (q.get("type") === "give_up") return giveUpStep(doc, q);
  if (byTestId(doc, "me-leaves-submit")) return requestFormStep(doc, q);
  // The request entry on Tasks: Report off sick for a sick day, else Request leave.
  const entry =
    (q.get("type") === "mc" ? byTestId(doc, "tasks-request-mc") : null) ??
    byTestId(doc, "tasks-request-leave") ??
    buttonByText(doc, PHONE_ENTRY);
  if (entry) {
    press(entry);
    return false;
  }
  press(byTestId(doc, "member-app-tab-tasks"));
  return false;
}

/**
 * The control the phone's next press hits (#2962), as `sendOnPhone` would
 * press it, or null while the phone is not open and drawn yet.
 */
export function phonePressTarget(memberId?: string | null): HTMLElement | null {
  if (!document.querySelector('[data-testid="demo-phone-view"]')) return null;
  const frame = visiblePhoneFrame(memberId);
  const doc = frame?.contentDocument;
  if (!frame || !doc || doc.readyState !== "complete") return null;
  if (!doc.querySelector('[data-testid="member-app-tab-tasks"]')) return null;
  return firstPressOf(() => phoneStep(frame, doc));
}

/** Set a controlled input or select the way a keyboard would, so React sees it. The pointer goes to the field first. */
function setControlValue(el: HTMLInputElement | HTMLSelectElement, value: string): void {
  if (el.value === value) return;
  void pointAndAct(el, () => writeValue(el, value));
}

function writeValue(el: HTMLInputElement | HTMLSelectElement, value: string): void {
  const proto = el instanceof HTMLSelectElement ? HTMLSelectElement : HTMLInputElement;
  Object.getOwnPropertyDescriptor(proto.prototype, "value")?.set?.call(el, value);
  el.dispatchEvent(
    new Event(el instanceof HTMLSelectElement ? "change" : "input", { bubbles: true }),
  );
}

/** Commit a field that saves on Enter or blur (the station drawer's number rows). */
function commitField(el: HTMLInputElement): void {
  el.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
}

/**
 * Open a Radix menu by its trigger, as Autoplay (#2824). The trigger opens on
 * pointerdown, not on click. The pointer goes to the trigger first.
 */
export function pressMenuTrigger(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  void pointAndAct(el, () => {
    synthetic = true;
    try {
      const init = { bubbles: true, cancelable: true, button: 0, ctrlKey: false };
      const down =
        typeof PointerEvent === "function"
          ? new PointerEvent("pointerdown", { ...init, pointerType: "mouse" })
          : new MouseEvent("pointerdown", init);
      el.dispatchEvent(down);
    } finally {
      synthetic = false;
    }
  });
  return true;
}

/** The station name of a person card's station chip, from its test id. */
function chipStation(chip: HTMLElement): string {
  const id = chip.getAttribute("data-testid") ?? "";
  const at = id.indexOf("-station-");
  return at < 0 ? "" : id.slice(at + "-station-".length);
}

/**
 * The "Make the change" step's autoplay action (#2824): use the real controls
 * the guide lights. A person: the level (for a joiner), then the station
 * chips; the card saves once they match. A station: Floor / session (filled
 * in already), Review & apply, then Apply. Returns true once the last press
 * is on its way, false while the next control is not on the page yet.
 */
export function fillAndSaveChange(toMake: DemoChangeToMake): boolean {
  if (toMake.kind === "station") {
    const apply = document.querySelector<HTMLElement>('[data-testid="review-apply"]');
    if (apply) return !apply.hasAttribute("disabled") && press(apply);
    const want = toMake.required_per_session;
    const field = toMake.station
      ? document.querySelector<HTMLInputElement>(
          `[data-testid="drawer-${toMake.station}-required"]`,
        )
      : null;
    if (field && want != null && field.value !== String(want)) {
      setControlValue(field, String(want));
      void pointAndAct(field, () => commitField(field));
      return false;
    }
    const review = document.querySelector<HTMLElement>('[data-testid="draft-review"]');
    if (review) press(review);
    return false;
  }
  const control = firstShown(DEMO_CHANGE_CONTROL_ANCHOR);
  if (!control) return false;
  if (control instanceof HTMLSelectElement) {
    if (toMake.tier) setControlValue(control, toMake.tier);
    return false;
  }
  const want = new Set(toMake.eligible_stations ?? []);
  for (const chip of Array.from(control.querySelectorAll<HTMLElement>("button[aria-pressed]"))) {
    if ((chip.getAttribute("aria-pressed") === "true") !== want.has(chipStation(chip))) {
      press(chip);
    }
  }
  return true;
}

/**
 * The control the change's first press hits (#2962), as `fillAndSaveChange`
 * would press it: the station's Apply, its field or Review & apply; the
 * person's level, or the first chip that does not match yet.
 */
export function changeFirstTarget(toMake: DemoChangeToMake): HTMLElement | null {
  if (toMake.kind === "station") {
    const apply = document.querySelector<HTMLElement>('[data-testid="review-apply"]');
    if (apply) return apply;
    const want = toMake.required_per_session;
    const field = toMake.station
      ? document.querySelector<HTMLInputElement>(
          `[data-testid="drawer-${toMake.station}-required"]`,
        )
      : null;
    if (field && want != null && field.value !== String(want)) return field;
    return document.querySelector<HTMLElement>('[data-testid="draft-review"]');
  }
  const control = firstShown(DEMO_CHANGE_CONTROL_ANCHOR);
  if (!control) return null;
  if (control instanceof HTMLSelectElement) {
    return toMake.tier && control.value !== toMake.tier ? control : null;
  }
  const want = new Set(toMake.eligible_stations ?? []);
  return (
    Array.from(control.querySelectorAll<HTMLElement>("button[aria-pressed]")).find(
      (chip) => (chip.getAttribute("aria-pressed") === "true") !== want.has(chipStation(chip)),
    ) ?? null
  );
}

function makeChange(known?: DemoChangeToMake | null): void {
  // The guide holds the change already: the first press goes as the pointer arrives (#2962).
  if (known) {
    poll(() => fillAndSaveChange(known), ACTION_GIVE_UP_MS - 1000);
    return;
  }
  void apiFetch<{ to_make?: DemoChangeToMake | null }>("/api/demo-sessions/current")
    .then((state) => {
      const toMake = state.to_make;
      if (toMake) poll(() => fillAndSaveChange(toMake), ACTION_GIVE_UP_MS - 1000);
    })
    .catch(() => undefined);
}

/** A dialog's button by its words, in any open dialog but the guide card. */
function dialogButton(pattern: RegExp): HTMLElement | null {
  for (const dialog of Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]'))) {
    if (dialog.getAttribute("data-testid") === "demo-guide-card") continue;
    const button = buttonByText(dialog, pattern);
    if (button) return button;
  }
  return null;
}

/**
 * Add the new joiner the way the visitor does (#2824): More, then Add manual
 * member, then Add member in the dialog (the name is filled in).
 */
function addMember(): void {
  let menuAt = 0;
  let itemAt = 0;
  poll(() => {
    const add = dialogButton(/^add member$/i);
    if (add) return !add.hasAttribute("disabled") && press(add);
    const item = document.querySelector<HTMLElement>(
      '[data-testid="members-table-add-manual-button"]',
    );
    if (item && !item.hasAttribute("data-disabled")) {
      if (Date.now() - itemAt > 1500) {
        itemAt = Date.now();
        press(item);
      }
      return false;
    }
    if (Date.now() - menuAt > 1500) {
      menuAt = Date.now();
      pressMenuTrigger(firstShown(PEOPLE_ADD_MEMBER_ANCHOR));
    }
    return false;
  }, ACTION_GIVE_UP_MS - 1000);
}

/**
 * Publish roster, then its confirm. With nothing new on screen the toolbar has
 * no Publish button: Publish sits in the More menu ("Publish to members").
 */
function publishRoster(): void {
  // The phone opens by itself once the roster is published: no panel may be open then.
  closeSidePanels();
  let opened = 0;
  let pressed = false;
  poll(() => {
    if (pressed) return pressTestId("publish-review-confirm");
    if (pressAnchor("roster-publish")) {
      pressed = true;
      return false;
    }
    const item = document.querySelector<HTMLElement>('[data-testid="roster-publish-trigger"]');
    if (item) {
      pressed = press(item);
      return false;
    }
    if (Date.now() - opened > 1500) {
      opened = Date.now();
      pressTestId("cycle-toolbar-more");
    }
    return false;
  }, ACTION_GIVE_UP_MS - 1000);
}

/**
 * Accept the scenario person's request on Home: open the review from its
 * "waiting" link when the list is not on the page yet, then press Accept into
 * plan on that person's row.
 */
function acceptOnHome(memberId?: string | null): void {
  let opened = 0;
  poll(() => {
    const mine = memberId
      ? document.querySelector<HTMLElement>(
          `[data-tour="leave-review-accept"][data-member-id="${memberId}"]`,
        )
      : null;
    const accept = mine ?? (memberId ? null : firstShown("leave-review-accept"));
    if (accept && !accept.hasAttribute("disabled")) return press(accept);
    // Needs you's Review, or the waiting line's link under Leave and requests.
    const link =
      document.querySelector<HTMLElement>('[data-testid="needs-action-leave"]') ??
      document.querySelector<HTMLElement>('[data-testid="review-waiting-link"]');
    if (link && Date.now() - opened > 2500) {
      opened = Date.now();
      press(link);
    }
    return false;
  }, ACTION_GIVE_UP_MS - 1000);
}

/**
 * Confirm the swap: on Home, Needs you links to the swaps beside the roster
 * (Approve). There, press Confirm swap.
 */
function confirmSwap(): void {
  let opened = 0;
  poll(() => {
    const confirm = firstShown("swap-confirm");
    if (confirm) return !confirm.hasAttribute("disabled") && press(confirm);
    const link = document.querySelector<HTMLElement>('[data-testid="needs-action-swap"]');
    if (link && Date.now() - opened > 2500) {
      opened = Date.now();
      press(link);
    }
    return false;
  }, ACTION_GIVE_UP_MS - 1000);
}

/** The top bar's "Change the scenario" button (#2962). */
export function scenarioButton(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-tour="nav-change-scenario"]');
}

/** True while Change the scenario is locked: the roster is not solved yet. */
export function scenarioLocked(): boolean {
  return scenarioButton()?.getAttribute("aria-disabled") === "true";
}

/** The "Change the scenario" dialog is open. */
export function scenarioDialogOpen(): boolean {
  return document.querySelector('[data-testid="demo-scenario-dialog"]') !== null;
}

function firstShown(name: string): HTMLElement | undefined {
  return Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`)).find(shown);
}

/**
 * The element the first Autoplay press of a step hits, or null when the step
 * presses nothing on the page (#2962). The pointer goes there as the step's
 * bar starts, and the press lands as it arrives. It follows `performAction`.
 */
export function firstPressTarget(
  step: GuideStep,
  memberId?: string | null,
  toMake?: DemoChangeToMake | null,
): HTMLElement | null {
  const done = step.done;
  if (done.type === "route") {
    const el = firstShownOf(step.anchors);
    return el ? (el.closest<HTMLElement>("a") ?? el.querySelector<HTMLElement>("a") ?? el) : null;
  }
  if (done.type !== "action") return null;
  switch (done.action) {
    case "solved":
      return document.querySelector('[data-tour="roster-solve"][aria-pressed="true"]')
        ? solveConfirm()
        : (firstShown("roster-solve") ?? null);
    case "published": {
      // Publish on the toolbar, else the open More menu's item, else More (#2962).
      const boxed = (el: HTMLElement | null | undefined) => (el && hasBox(el) ? el : null);
      return (
        boxed(
          Array.from(document.querySelectorAll<HTMLElement>('[data-tour="roster-publish"]')).find(
            hasBox,
          ),
        ) ??
        boxed(document.querySelector<HTMLElement>('[data-testid="roster-publish-trigger"]')) ??
        document.querySelector<HTMLElement>('[data-testid="cycle-toolbar-more"]') ??
        firstShown("roster-publish") ??
        null
      );
    }
    case "phone-opened":
      return document.querySelector('[data-testid="demo-phone-view"]')
        ? null
        : (firstShown("demo-phone-toggle") ?? null);
    case "request-sent":
      return phonePressTarget(memberId);
    case "member-added":
      return (
        dialogButton(/^add member$/i) ??
        document.querySelector<HTMLElement>('[data-testid="members-table-add-manual-button"]') ??
        firstShown(PEOPLE_ADD_MEMBER_ANCHOR) ??
        null
      );
    case "request-accepted": {
      const mine = memberId
        ? document.querySelector<HTMLElement>(
            `[data-tour="leave-review-accept"][data-member-id="${memberId}"]`,
          )
        : null;
      return (
        mine ??
        (memberId ? null : firstShown("leave-review-accept")) ??
        document.querySelector<HTMLElement>('[data-testid="needs-action-leave"]') ??
        document.querySelector<HTMLElement>('[data-testid="review-waiting-link"]')
      );
    }
    case "swap-confirmed":
      return (
        firstShown("swap-confirm") ??
        document.querySelector<HTMLElement>('[data-testid="needs-action-swap"]')
      );
    case "change-made":
      // The level, a chip, the station row or the review; else the lit control.
      return toMake ? changeFirstTarget(toMake) : null;
    case "scenario-opened":
      return scenarioLocked() || scenarioDialogOpen() ? null : scenarioButton();
    default:
      return firstShownOf(step.anchors);
  }
}

function hasBox(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}

function firstShownOf(names: readonly string[] | undefined): HTMLElement | null {
  for (const name of names ?? []) {
    const el = firstShown(name);
    if (el) return el;
  }
  return null;
}

/**
 * Do what the visitor does for a step that waits for an action. Best effort:
 * the guide moves on by itself when the action does not complete in time.
 */
export function performAction(
  action: DemoGuideAction,
  step: GuideStep,
  memberId?: string | null,
  toMake?: DemoChangeToMake | null,
): void {
  switch (action) {
    case "solved":
      // Solve opens and closes its console: press it only while it is closed.
      if (!document.querySelector('[data-tour="roster-solve"][aria-pressed="true"]')) {
        pressAnchor("roster-solve");
      }
      poll(() => press(solveConfirm()), 6000);
      return;
    case "published":
      publishRoster();
      return;
    case "phone-opened":
      pressPhoneToggle();
      return;
    case "request-sent":
      sendOnPhone(memberId);
      return;
    case "change-made":
      makeChange(toMake);
      return;
    case "member-added":
      addMember();
      return;
    case "request-accepted":
      acceptOnHome(memberId);
      return;
    case "swap-confirmed":
      confirmSwap();
      return;
    case "scenario-opened":
      // Open the scenario picker; the guide sees the dialog and ends the tour.
      if (!scenarioLocked() && !scenarioDialogOpen()) press(scenarioButton());
      return;
    default:
      for (const name of step.anchors ?? []) if (pressAnchor(name)) return;
  }
}
